import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header, SectionLabel } from "../components/Brand";
import { api, formatIST, BACKEND_URL } from "../lib/api";
import {
  Loader2,
  Search,
  Download,
  AlertTriangle,
  LogOut,
  ExternalLink,
  ChevronRight,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = (s) => {
  if (s === "submitted")
    return "bg-green-500/10 text-green-400 border border-green-500/20";
  if (s === "expired_no_submit")
    return "bg-red-500/10 text-red-400 border border-red-500/20";
  return "bg-white/10 text-zinc-300 border border-white/15";
};

const STATUS_LABEL = (s) => {
  if (s === "submitted") return "Submitted";
  if (s === "expired_no_submit") return "Late / No submit";
  return "In progress";
};

const fmtDuration = (sec) => {
  if (!sec && sec !== 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({ total: 0, submitted: 0, in_progress: 0, expired: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [branch, setBranch] = useState("");
  const [year, setYear] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status_filter = statusFilter;
      if (branch) params.branch = branch;
      if (year) params.year = year;
      params.sort_by = sortBy;
      params.order = order;
      const res = await api.get("/admin/candidates", { params });
      setCandidates(res.data.candidates || []);
      setStats(res.data.stats || {});
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("bh_admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load candidates");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branch, year, sortBy, order, navigate]);

  useEffect(() => {
    const t = localStorage.getItem("bh_admin_token");
    if (!t) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        (c.full_name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.candidate_code || "").toLowerCase().includes(q)
    );
  }, [candidates, search]);

  const branches = useMemo(() => {
    return Array.from(new Set(candidates.map((c) => c.branch).filter(Boolean))).sort();
  }, [candidates]);
  const years = useMemo(() => {
    return Array.from(new Set(candidates.map((c) => c.year).filter(Boolean))).sort();
  }, [candidates]);

  const logout = () => {
    localStorage.removeItem("bh_admin_token");
    localStorage.removeItem("bh_admin_email");
    navigate("/admin");
  };

  const exportCsv = (shortlistedOnly = false) => {
    const token = localStorage.getItem("bh_admin_token");
    const url = `${BACKEND_URL}/api/admin/export.csv?token=${encodeURIComponent(token)}${shortlistedOnly ? "&only_shortlisted=true" : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="admin-dashboard">
      <Header
        right={
          <div className="flex items-center gap-4">
            <span className="text-xs text-zinc-500 hidden sm:block">
              {localStorage.getItem("bh_admin_email")}
            </span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        }
      />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <SectionLabel className="mb-3">Admin Console</SectionLabel>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
              Candidate submissions
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="cv-vault-btn"
              onClick={() => navigate("/admin/files")}
              className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white font-bold uppercase tracking-wider px-5 py-2.5 text-xs hover:bg-white/5 rounded-sm"
            >
              <FileText className="w-4 h-4" />
              CV Vault
            </button>
            <button
              data-testid="export-all-csv-btn"
              onClick={() => exportCsv(false)}
              className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-wider px-5 py-2.5 text-xs hover:bg-zinc-200 rounded-sm"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              data-testid="export-shortlist-csv-btn"
              onClick={() => exportCsv(true)}
              className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white font-bold uppercase tracking-wider px-5 py-2.5 text-xs hover:bg-white/5 rounded-sm"
            >
              Shortlist CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
          {[
            { label: "Total", value: stats.total },
            { label: "Submitted", value: stats.submitted, tone: "text-green-400" },
            { label: "In progress", value: stats.in_progress, tone: "text-yellow-400" },
            { label: "Expired", value: stats.expired, tone: "text-red-400" },
          ].map((s) => (
            <div key={s.label} className="bg-[#09090b] p-5">
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                {s.label}
              </div>
              <div className={`font-display font-black text-3xl ${s.tone || "text-white"}`}>
                {s.value ?? 0}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-zinc-500" />
            <input
              data-testid="search-input"
              className="bh-input pl-10"
              placeholder="Search by name, email, code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bh-input max-w-[180px]"
          >
            <option value="all">All statuses</option>
            <option value="submitted">Submitted</option>
            <option value="not_submitted">Not submitted</option>
            <option value="late">Late (expired)</option>
          </select>
          <select
            data-testid="branch-filter"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            className="bh-input max-w-[200px]"
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            data-testid="year-filter"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="bh-input max-w-[160px]"
          >
            <option value="">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            data-testid="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bh-input max-w-[180px]"
          >
            <option value="created_at">Sort: Created</option>
            <option value="submitted_at">Sort: Submitted</option>
            <option value="time_taken">Sort: Time taken</option>
            <option value="branch">Sort: Branch</option>
            <option value="full_name">Sort: Name</option>
          </select>
          <button
            data-testid="order-toggle"
            onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
            className="bh-input max-w-[110px] cursor-pointer text-left uppercase text-xs tracking-wider"
          >
            {order === "desc" ? "↓ Desc" : "↑ Asc"}
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="candidates-table">
              <thead>
                <tr className="border-b border-white/10 text-[0.65rem] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="text-left px-4 py-3 font-semibold">Code</th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Branch / Year</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Started</th>
                  <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                  <th className="text-left px-4 py-3 font-semibold">Time</th>
                  <th className="text-left px-4 py-3 font-semibold">Score</th>
                  <th className="text-left px-4 py-3 font-semibold">Flags</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-zinc-500">
                      <Loader2 className="inline w-5 h-5 animate-spin" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-zinc-500 text-sm">
                      No candidates yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => navigate(`/admin/candidate/${c.id}`)}
                      data-testid={`row-${c.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-zinc-400">{c.candidate_code}</td>
                      <td className="px-4 py-3 text-white font-semibold">{c.full_name}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{c.email}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">
                        {c.branch}
                        <div className="text-zinc-600">{c.year}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[0.65rem] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${STATUS_BADGE(c.submission_status)}`}>
                          {STATUS_LABEL(c.submission_status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-400">{formatIST(c.start_at)}</td>
                      <td className="px-4 py-3 text-xs text-zinc-400">{c.submitted_at ? formatIST(c.submitted_at) : "—"}</td>
                      <td className="px-4 py-3 text-xs font-mono">{fmtDuration(c.time_taken_seconds)}</td>
                      <td className="px-4 py-3 text-xs">
                        {c.scoring?.total_score != null ? (
                          <span className="text-white font-bold">{c.scoring.total_score}/100</span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(c.red_flags || []).length > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-red-500/10 text-red-400 border border-red-500/20"
                            title={c.red_flags.join(" • ")}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {c.red_flags.length}
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-500">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
